import { adminEventRuntimeApiSdk } from '@hds/admin-client-pinia-vue-sdk';
import { configureAdministrationBackend } from '@hds/administration-client-sdk';
import { eventRuntimeApiSdk } from '@hds/client-pinia-vue-sdk';
import {
    IDENTITY_PATHS,
    authorizationHeaders,
    configureIdentityClient,
    createUnauthorizedHandler
} from '@hds/identity-client-sdk';

import { autoAuctionApiSdk } from './autoAuctionClient.js';
import { EVENT_RUNTIME_API, instrumentApiSdk } from './requestLogging.js';

/**
 * Where the backends are, in one place.
 *
 * There are FIVE of them, which is the fact this module exists to make
 * manageable:
 *
 *   vibe-server REST        the catalog, sales, saved, chat, and the identity
 *                           endpoints (`/session/*`)
 *   vibe-server WebSocket   chat delivery — supplied as the realtime PORT, not
 *                           here, because it is a socket the host owns and hands in
 *   event runtime REST      bidding-server (bids, proxies, engine status) — two
 *                           clients read it, the bidder's and the clerk's, and
 *                           both are pointed here
 *   delta-server socket     the change feed that makes a bid land on a card
 *   administration server   the five administration nouns
 *
 * A second surface would have had to rediscover all of that. Now it calls one
 * function — and it calls it with what `discoverServices` answered, because the
 * two speak the same shape.
 *
 * **The host still owns the environment.** Nothing here reads `import.meta.env`
 * or `process.env`; what a host passes in came from its manifest (see
 * serviceDiscovery.js) or from a test.
 *
 * **The credential is written once, here.** Every backend that authenticates
 * carries the session's platform token and a proof made for that one request
 * (the identity ADR), and every one of them shares ONE single-flight 401 handler — one redirect per page load however many requests
 * fail together, standing down during sign-out so the hosted-logout navigation
 * survives. A host may still pass its own `authHeader` / `onUnauthorized` per
 * backend; none has to.
 */

// The one runtime endpoint the runtime's own SDK does not hold. It is read by
// biddingSource rather than by a client object, so it lives here.
//
// There were three more: a tools URL, a dev API key and an in-network bidding
// address, all of them there to let the app wipe and regenerate the event
// runtime. Resetting the runtime is an operator action — Postman, or curl —
// and shipping the credential for it inside a client that every surface
// imports was the wrong place for it twice over.
const runtimeEndpoints = {
    // The delta-server change feed. Without it a bid never reaches a card:
    // every live update in the app arrives on this socket.
    deltaWebSocketUrl: null
};

/**
 * The session's credential, on every request that authenticates: the platform
 * token and a fresh proof for this request's method and URL. No header when
 * signed out — the request still goes, the server answers 401, and the handler
 * below sends the person to the login screen.
 */
export async function sessionAuthHeader({ method, url }) {
    return authorizationHeaders({ method, url });
}

// One latch for the whole page load: a burst of 401s from any backend is one
// redirect, ever. Module scope on purpose — the latch must outlive every
// configureBackends call, or a second call would re-arm it.
const sessionUnauthorized = createUnauthorizedHandler({ redirectTo: IDENTITY_PATHS.login });

/**
 * Point the SDK at its backends.
 *
 * Every block is optional and merges onto what is already set, so a host can
 * configure the catalog at boot and the runtime once it knows where the runtime
 * is. A block that is given gets the shared credential and 401 handler unless
 * it names its own. Called once from a host's entry point, beside
 * `configurePorts` — typically `configureBackends(await discoverServices())`.
 *
 * @param {Object} config                                The manifest shape (see serviceDiscovery.js); every block optional.
 * @param {string} [config.environment]                  Named by the manifest; carried through, read by nobody here.
 * @param {Object} [config.vibeServer]                   { baseUrl, authHeader?, onUnauthorized? } — the REST API.
 * @param {Object} [config.administrationServer]         { baseUrl, authHeader?, onUnauthorized? }
 * @param {Object} [config.eventRuntime]                 { biddingUrl, deltaWebSocketUrl, authHeader? } — both runtime clients.
 * @param {Object} [config.identityServer]               { baseUrl, appId } — where a person signs in, which app this surface is, and the person's own session routes.
 */
export function configureBackends({ environment, vibeServer, administrationServer, eventRuntime, identityServer } = {}) {
    if (vibeServer || identityServer) {
        const server = vibeServer ?? {};
        // The identity client owns its own transport. Its routes (/session/*) are the
        // identity server's; its credential and its 401 handling are the same as the
        // domain client's, so the vibe-server overrides apply to both.
        configureIdentityClient({
            baseUrl: identityServer?.baseUrl,
            authHeader: server.authHeader ?? sessionAuthHeader,
            onUnauthorized: server.onUnauthorized ?? sessionUnauthorized,
            identityServer
        });
    }
    if (vibeServer) {
        if (vibeServer.baseUrl !== undefined) autoAuctionApiSdk.baseAutoAuctionUri = vibeServer.baseUrl;
        autoAuctionApiSdk.customHeaderFn = vibeServer.authHeader ?? sessionAuthHeader;
        autoAuctionApiSdk.onUnauthorized = vibeServer.onUnauthorized ?? sessionUnauthorized;
    }

    if (administrationServer) {
        configureAdministrationBackend({
            baseUrl: administrationServer.baseUrl,
            authHeader: administrationServer.authHeader ?? sessionAuthHeader,
            onUnauthorized: administrationServer.onUnauthorized ?? sessionUnauthorized
        });
    }

    if (eventRuntime) {
        // The runtime's two SDKs — the bidder's and the clerk's — configured
        // through their property setters rather than replaced. They belong to
        // the event runtime and are not ours to change; this wraps them so a
        // host has one call instead of three vocabularies. The runtime
        // authenticates the same session credential vibe-server does (one
        // identity mechanism, ruled 2026-09-21).
        const authHeader = eventRuntime.authHeader ?? sessionAuthHeader;
        const runtimeAuthHeader = (request = {}) => authHeader(request);
        for (const runtimeSdk of [eventRuntimeApiSdk, adminEventRuntimeApiSdk]) {
            if (eventRuntime.biddingUrl !== undefined) runtimeSdk.baseBiddingUri = eventRuntime.biddingUrl;
            runtimeSdk.customHeaderFn = runtimeAuthHeader;
            // And reports every call it makes, the way the vibe-server client
            // reports its own. Not ours to edit means it cannot report from the
            // inside, so it is wrapped from out here — and here specifically,
            // because this is already the one function that touches these
            // clients. `instrumentApiSdk` refuses to wrap the same client twice.
            instrumentApiSdk(runtimeSdk, { api: EVENT_RUNTIME_API });
        }
        for (const key of Object.keys(runtimeEndpoints)) {
            if (eventRuntime[key] !== undefined) runtimeEndpoints[key] = eventRuntime[key];
        }
    }

    return {
        environment,
        vibeServer: { baseUrl: autoAuctionApiSdk.baseAutoAuctionUri },
        eventRuntime: { ...runtimeEndpoints }
    };
}

/** What the runtime endpoints are currently set to. Read by biddingSource. */
export function useRuntimeEndpoints() {
    return runtimeEndpoints;
}

/**
 * Put every runtime endpoint back to its default. For tests — configuration
 * left behind by one case is the kind of leak that makes the next one pass for
 * the wrong reason.
 */
export function resetBackends() {
    runtimeEndpoints.deltaWebSocketUrl = null;
    return runtimeEndpoints;
}
